import { avaliador, type Arvore, type Valores } from "./metasArvore";
import { fracaoContribuicao } from "./metasFormato";

/** Uma linha do relatório comercial: meta de uma seção (N2) numa loja, no mesmo grão do `comercial_*.csv` do cliente. */
export interface LinhaRelatorioComercial {
  /** AAAAMM do mês da meta (202607). */
  mesAno: number;
  loja: number | null;
  departamento: string;
  secao: string;
  metaFaturamento: number;
  metaContribuicao: number;
  metaPerda: number;
}

/**
 * Ordenado pela maior meta de faturamento (como o `comercial_*.csv`), com loja,
 * departamento e seção como desempate.
 *
 * Loja × departamento (N1) × seção (N2) a partir da árvore já carregada até o
 * N2 de cada loja em `lojas`. Faturamento é a meta ajustada (o que o usuário
 * informou, ou o calculado sem ajuste). Contribuição e perda vêm do motor
 * para o valor calculado do nó e acompanham o ajuste na mesma proporção —
 * a contribuição pelo % que o motor resolveu (mesma conta da coluna "%
 * Contribuição" da tela), a perda pela razão perda ÷ venda calculadas. Nó
 * com venda calculada zerada não tem proporção: fica o valor do motor.
 */
export function linhasRelatorioComercial(
  arvore: Arvore,
  ajustes: Valores,
  lojas: string[],
  anoMeta: number,
  mesMeta: number,
): LinhaRelatorioComercial[] {
  const { nos, filhos } = arvore;
  const { ajustada } = avaliador(arvore, ajustes);
  const mesAno = anoMeta * 100 + mesMeta;
  const linhas: LinhaRelatorioComercial[] = [];

  lojas.forEach((idLoja) => {
    (filhos[idLoja] ?? []).forEach((idN1) => {
      const n1 = nos[idN1];
      if (!n1) return;
      (filhos[idN1] ?? []).forEach((idN2) => {
        const n2 = nos[idN2];
        if (!n2) return;
        const meta = ajustada(idN2);
        const fracaoContrib = fracaoContribuicao([
          { percEnviado: n2.percContribuicaoEnviado, contribuicao: n2.contribuicaoCalculada, calculada: n2.calculado },
        ]);
        const metaContribuicao = fracaoContrib !== null && n2.calculado ? meta * fracaoContrib : n2.contribuicaoCalculada;
        const metaPerda = n2.calculado ? (meta * n2.perdaCalculada) / n2.calculado : n2.perdaCalculada;
        linhas.push({
          mesAno,
          loja: n2.codEmpresa,
          departamento: n1.nome,
          secao: n2.nome,
          metaFaturamento: meta,
          metaContribuicao: Math.round(metaContribuicao * 100) / 100,
          metaPerda: Math.round(metaPerda * 100) / 100,
        });
      });
    });
  });

  return linhas.sort(
    (a, b) =>
      b.metaFaturamento - a.metaFaturamento ||
      (a.loja ?? 0) - (b.loja ?? 0) ||
      a.departamento.localeCompare(b.departamento) ||
      a.secao.localeCompare(b.secao),
  );
}
